import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import useUploadFileMutation from 'api/hooks/useUploadFileMutation';
import { Emitter } from 'app/socket';
import { logError } from 'lib/sentry/logError';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';

import { DURATION_HEADER_NAME } from 'features/chat/config';
import { UnuploadedAttachment } from 'features/chat/config/types/Attachment';

export const useAttachmentsUpload = (
  attachments: UnuploadedAttachment[] = []
) => {
  const [successfulAttachments, setSuccessfulAttachments] = useState(
    [] as (Attachment & { file: File })[]
  );
  const [successfulMutations, setSuccessfulMutations] = useState(
    [] as Attachment[]
  );
  const [attachmentsQueue, setAttachmentsQueue] = useState(
    [] as (Attachment & { file: File })[]
  );

  const { request } = useMutationHTTPRequest();

  const mutation = useUploadFileMutation({
    onSuccess: () => {
      const attachment = attachmentsQueue[0];

      setSuccessfulMutations((oldMutations) => [
        ...oldMutations,
        {
          url: attachment.url,
          name: attachment.name,
          contentType: attachment.contentType,
          contentLength: attachment.contentLength,
          key: attachment.attachmentId,
        },
      ]);
      setAttachmentsQueue((oldQueue) => oldQueue.slice(1));
    },
    onError: (error) => {
      logError(error);
      Emitter.emit('chat_message_attachments_uploaded_failed');
    },
  });

  const attachmentsQueries = useQueries({
    queries: attachments.map((attachment) => {
      return {
        queryKey: ['attachment', attachment.name],
        queryFn: async () => {
          const response = await request({
            url: '/api/attachments/upload',
            headers: {
              'object-content-type': attachment.contentType,
            },
            ...(attachment.duration && {
              body: {
                metadata: {
                  duration: attachment.duration.toString(),
                },
              },
            }),
          });

          setSuccessfulAttachments((prev) => [
            ...prev,
            {
              ...attachment,
              url: response.presignedUrl,
              attachmentId: response.attachmentId,
            },
          ]);
          return response;
        },
        onSuccess: (data: { key: string; presignedUrl: string }) => {
          setAttachmentsQueue((oldQueue) => [
            ...oldQueue,
            { ...attachment, url: data.presignedUrl, key: data.key },
          ]);
        },
        onError: (error: Error) => {
          logError(error);
          Emitter.emit('chat_message_attachments_uploaded_failed');
        },
      };
    }),
  });

  useEffect(() => {
    if (attachmentsQueue.length > 0 && !mutation.isLoading) {
      mutation.mutate({
        file: attachmentsQueue[0].file,
        key: attachmentsQueue[0].attachmentId as string,
        presignedUrl: attachmentsQueue[0].url,
        headers: {
          ...(attachmentsQueue[0].duration && {
            [DURATION_HEADER_NAME]: attachmentsQueue[0].duration,
          }),
        },
      });
    }
  }, [attachmentsQueue, mutation]);

  useEffect(() => {
    if (
      successfulAttachments.length &&
      successfulMutations.length &&
      successfulAttachments.length === successfulMutations.length
    ) {
      Emitter.emit(
        'chat_message_attachments_uploaded',
        attachments.map((attachment) => {
          const successAttachment = successfulAttachments.find(
            (successAtt) => successAtt.name === attachment.name
          );
          return {
            name: attachment.name,
            contentType: attachment.contentType,
            contentLength: attachment.contentLength,
            duration: attachment.duration,
            url: successAttachment?.url,
            attachmentId: successAttachment?.attachmentId,
            // TODO: remove key after BE removes in on their side
            key: successAttachment?.attachmentId,
          };
        })
      );
    }
  }, [successfulMutations, successfulAttachments, attachments]);

  return attachmentsQueries;
};
